/* GFI Nexus — Accuracy Dashboard calc (Fase D of "FOB PRICE FORECAST v2")
   ─────────────────────────────────────────────────────────────────────
   No new table needed: ai_analyst_log ALREADY accumulates one real
   snapshot per day per category (base_forecast, adjusted_price, and
   context.lastRealPrice — whatever price was actually known that day,
   manual quote or market feed). That's exactly what a projection-vs-
   reality comparison needs: pick a past entry's 1-week-ahead forecast,
   find the entry ~7 days later, and treat ITS lastRealPrice as "what
   actually happened."

   This only works going FORWARD from whenever ai_analyst_log started
   accumulating real daily rows (2026-09-15) — there is no backfilled
   forecast history to compare against, so accuracy starts genuinely
   empty and fills in day by day. Never fabricate a MAPE number from
   less data than this needs (same rule as fobPricesForecast.js's
   trend-only/seasonal gate) — computeAccuracy() reports `sufficient:
   false` per category until there's at least MIN_MATCHES real matched
   pairs, and the UI must show that honestly instead of a misleading
   number. */

var HORIZON_DAYS = 7; // base_forecast/adjusted_price are 1-week-ahead
var MATCH_TOLERANCE_DAYS = 2; // accept a "realized" entry within ±2 days of the exact 7-day mark (daily cron can skip a day)
var MIN_MATCHES = 2; // fewer than this and a MAPE% is not meaningful — say so instead of showing a number
var MAX_MATCHES_FOR_MAPE = 12; // "last 12 weeks" per the roadmap spec

function daysBetween(a, b) {
  return Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
}

function average(nums) {
  return nums.length ? nums.reduce(function (s, v) { return s + v; }, 0) / nums.length : null;
}

function round2(n) {
  return n == null ? null : Math.round(n * 100) / 100;
}

/* entries: array of ai_analyst_log records (loadFromSupabase shape) for
   ONE category, already filtered — { logDate, baseForecast,
   adjustedPrice, context: { lastRealPrice } }. Returns matched pairs +
   MAPE for base vs Claude-adjusted, sorted oldest-first. */
function matchProjectionsToReality(entries) {
  var sorted = entries.slice().sort(function (a, b) { return a.logDate < b.logDate ? -1 : 1; });
  var matches = [];
  sorted.forEach(function (entry) {
    if (entry.baseForecast == null) return;
    var targetDate = null;
    try {
      var d = new Date(entry.logDate + "T00:00:00");
      d.setDate(d.getDate() + HORIZON_DAYS);
      targetDate = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    } catch (e) { return; }

    var best = null, bestDelta = Infinity;
    sorted.forEach(function (candidate) {
      if (candidate === entry) return;
      var realized = candidate.context && candidate.context.lastRealPrice;
      if (realized == null) return;
      var delta = Math.abs(daysBetween(targetDate, candidate.logDate));
      if (delta <= MATCH_TOLERANCE_DAYS && delta < bestDelta) { best = candidate; bestDelta = delta; }
    });
    if (!best) return;

    var realized = best.context.lastRealPrice;
    if (!realized) return; // guard against div-by-zero / nonsensical $0 realized price
    var baseErrorPct = round2(Math.abs(realized - entry.baseForecast) / realized * 100);
    var adjustedErrorPct = entry.adjustedPrice != null ? round2(Math.abs(realized - entry.adjustedPrice) / realized * 100) : null;
    matches.push({
      projectedDate: entry.logDate,
      realizedDate: best.logDate,
      baseForecast: entry.baseForecast,
      adjustedPrice: entry.adjustedPrice,
      realized: realized,
      baseErrorPct: baseErrorPct,
      adjustedErrorPct: adjustedErrorPct,
    });
  });
  return matches;
}

/* Returns { [category]: { matches, mapeBase, mapeAdjusted, sufficient } } */
export function computeAccuracy(allEntries) {
  var byCategory = {};
  (allEntries || []).forEach(function (e) {
    var cat = e.category || "Conventional"; // pre-split legacy rows had no category — treat as Conventional-ish, though none should remain in practice
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(e);
  });

  var out = {};
  Object.keys(byCategory).forEach(function (cat) {
    var matches = matchProjectionsToReality(byCategory[cat]);
    var recent = matches.slice(-MAX_MATCHES_FOR_MAPE);
    var sufficient = recent.length >= MIN_MATCHES;
    out[cat] = {
      matches: recent,
      mapeBase: sufficient ? round2(average(recent.map(function (m) { return m.baseErrorPct; }))) : null,
      mapeAdjusted: sufficient ? round2(average(recent.filter(function (m) { return m.adjustedErrorPct != null; }).map(function (m) { return m.adjustedErrorPct; }))) : null,
      sufficient: sufficient,
      matchCount: recent.length,
    };
  });
  return out;
}

export var ACCURACY_MIN_MATCHES = MIN_MATCHES;
export var ACCURACY_HORIZON_DAYS = HORIZON_DAYS;
export var ACCURACY_MAX_MATCHES = MAX_MATCHES_FOR_MAPE;
